import React, { FormEventHandler, useEffect } from 'react'

import { useMutation } from '@apollo/client'

import Toast from '$extensionSrc/components/Toast'
import { settingsPath } from '$extensionSrc/consts/urlConsts'
import useForm from '$extensionSrc/hooks/useForm'
import { hasMinLength } from '$extensionSrc/utils/stringValidator'
import { PasswordExceptionType } from '$serverSrc/consts/enums'

import FormSubmitButton from './FormSubmitButton'
import FormTextInput from './FormTextInput'
import { unauthClient } from '../apolloClientUnauth'
import { handleLoginResponse } from './LoginForm'
import { loginMutation, resetPasswordMutation } from '../data/queryUnauth'

import './PasswordResetForm.scss'

export default function PasswordResetForm({
  jwt,
  userEmail,
}: {
  jwt: string
  userEmail: string
}) {
  const defaultValues = { password: '', confirmPassword: '' }
  const requiredValidations = {
    password: (x: string) => hasMinLength(x, 16),
    confirmPassword: (x: string) => hasMinLength(x, 16),
  }

  const {
    getFieldChangeHandler,
    getRequiredValidationMetByField,
    isValid,
    values,
  } = useForm({
    defaultValues,
    requiredValidations,
  })

  const [
    submitResetRequest,
    { data: resetResponseData, loading: resetLoading },
  ] = useMutation(resetPasswordMutation, { client: unauthClient })

  const [submitLoginRequest, { data: loginResponseData }] = useMutation(
    loginMutation,
    { client: unauthClient },
  )

  const resetPwStatusCode = resetResponseData?.resetPassword?.statusCode
  const resetPwError = resetResponseData?.resetPassword?.error
  const loginStatusCode = loginResponseData?.login?.statusCode
  const loginError = loginResponseData?.login?.error
  const enterpriseSsoLoginUrl = loginResponseData?.login?.enterpriseSsoLoginUrl

  useEffect(() => {
    if (resetPwStatusCode === 200 && !resetPwError) {
      submitLoginRequest({
        variables: { email: userEmail, password: values.password },
      })
    }
  }, [
    resetPwStatusCode,
    resetPwError,
    submitLoginRequest,
    userEmail,
    values.password,
  ])

  useEffect(() => {
    if (loginStatusCode === 200) {
      handleLoginResponse(
        loginError,
        settingsPath,
        userEmail,
        enterpriseSsoLoginUrl,
      )
    }
  }, [loginStatusCode, loginError, userEmail, enterpriseSsoLoginUrl])

  const submitForm: FormEventHandler = (event) => {
    event.preventDefault()
    submitResetRequest({
      variables: {
        jwt,
        newPassword: values.password,
      },
    })
  }

  const passwordsMatch = values.confirmPassword === values.password

  return (
    <form className="PasswordResetForm" onSubmit={submitForm}>
      <div className="form-spacer" />
      <div className="form-header">
        <div className="form-title bold heading1">Reset Your Password</div>
        <div className="form-subtitle body1">
          Create a new password for your {userEmail} account.
        </div>
      </div>
      <div className="form-content">
        <FormTextInput
          placeholderText="New password"
          name="password"
          isSensitive
          value={values.password}
          handleChange={(value) => getFieldChangeHandler('password')(value)}
          requirements={[
            {
              text: 'Enter 16 characters or more',
              isFulfilled: getRequiredValidationMetByField('password'),
            },
          ]}
        />
        <FormTextInput
          placeholderText="Confirm password"
          name="confirmPassword"
          isSensitive
          value={values.confirmPassword}
          handleChange={(value) =>
            getFieldChangeHandler('confirmPassword')(value)
          }
          requirements={[
            {
              text: 'Match new password to confirm',
              isFulfilled: passwordsMatch,
            },
          ]}
        />
      </div>
      <FormSubmitButton isValid={isValid && passwordsMatch}>
        Reset
      </FormSubmitButton>
      {resetPwStatusCode !== 200 && resetPwError && (
        <Toast
          text={
            resetPwError === PasswordExceptionType.PASSWORD_TOO_SHORT_EXCEPTION
              ? 'Password must be at least 16 characters'
              : 'Failed to reset password'
          }
          color="red"
        />
      )}
      {resetPwStatusCode === 200 && (
        <Toast text="Password successfully reset!" />
      )}
      {resetLoading && <Toast text="Changing password..." />}
    </form>
  )
}
